// src/components/Layout/MainLayout.js
import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  Users, 
  Calendar, 
  Dog, 
  LayoutDashboard, 
  ShoppingCart, 
  Settings, 
  LogOut, 
  ChevronRight,
  Sparkles,
  Menu,
  X,
  Bell,
  Search,
  Briefcase,
  ShieldCheck,
  Lock,
  Activity,
  FileText,
  Layers,
  Package
} from 'lucide-react';

const MainLayout = ({ children }) => {
  const { user, logout, can, authLoading } = useAuth();
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const navigate = useNavigate();

  const menuItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Comando Visual', path: '/portal', icon: Sparkles },
    { name: 'Agenda', path: '/agenda', icon: Calendar },
    { name: 'Clientes', path: '/clientes', icon: Users },
    { name: 'Mascotas', path: '/mascotas', icon: Dog },
    { name: 'Usuarios', path: '/usuarios', icon: ShieldCheck },
    { name: 'Roles', path: '/roles', icon: Lock },
    { name: 'Permisos', path: '/permisos', icon: Activity },
    { name: 'Catálogos', path: '/catalogos', icon: Layers },
    { name: 'Productos', path: '/productos', icon: Package },
    { name: 'Servicios', path: '/servicios', icon: Briefcase },
    { name: 'Ventas', path: '/facturacion', icon: ShoppingCart },
    { name: 'Reportes', path: '/reportes', icon: FileText },
  ].filter(item => can(item.name));

  const [logoUrl, setLogoUrl] = useState(null);

  React.useEffect(() => {
    import('axios').then(axios => {
      axios.default.get('/backend/api/configuracion/branding.php')
        .then(res => {
          if (res.data.success) {
            setLogoUrl(res.data.data.logo_url);
          }
        })
        .catch(() => {});
    });
  }, []);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-950">
        <div className="flex flex-col items-center gap-6">
           <div className="w-16 h-16 border-4 border-primary/20 border-t-primary rounded-full animate-spin shadow-xl shadow-primary/20"></div>
           <p className="text-white/40 font-black uppercase tracking-[5px] text-xs animate-pulse italic">Descifrando Matriz de Privilegios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-transparent relative">
      {/* Overlay para cerrar en móvil al hacer click fuera */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[45] animate-in fade-in duration-300"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Mobile Menu Button (Trigger flotante cuando está cerrado) */}
      {!isSidebarOpen && (
        <button 
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden fixed top-4 left-4 z-[60] glass p-3 rounded-2xl text-white shadow-2xl animate-in zoom-in duration-300"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:relative inset-y-0 left-0 z-50 h-screen glass border-r border-white/5 transition-all duration-500 ease-in-out overflow-hidden ${isSidebarOpen ? 'w-72 translate-x-0 bubble-glow' : 'w-0 lg:w-0 -translate-x-full lg:translate-x-0 lg:border-none'}`}>
        <div className="w-72 flex flex-col h-full p-6">
          
          {/* Logo Area & Close Button for Mobile */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-3">
               <div className="w-12 h-12 rounded-2xl overflow-hidden glass border border-white/10 shadow-lg p-1 animate-pulse-slow">
                  {/* Sidebar muestra el logo de la EMPRESA (su negocio) */}
                  {logoUrl ? (
                    <img src={logoUrl} alt="Company Logo" className="w-full h-full object-contain p-1 rounded-xl" />
                  ) : (
                    <Dog size={24} className="text-white m-auto" />
                  )}
               </div>
               <div>
                <h1 className="text-xl font-black tracking-tight leading-none italic text-white uppercase">SISTEMA <span className="text-primary-light">PRO</span></h1>
                <p className="text-[10px] uppercase font-bold tracking-[3px] text-white/30 mt-1">Gobernanza Local</p>
              </div>
            </div>
            
            {/* Botón de cierre EXCLUSIVO móvil */}
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-xl bg-white/5 text-white/40 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-2 overflow-y-auto custom-scrollbar pr-2 -mr-2">
            {menuItems.map((item) => (
              <NavLink
                key={item.name}
                to={item.path}
                onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                className={({ isActive }) => `
                  group flex items-center justify-between px-4 py-4 rounded-2xl transition-all duration-300
                  ${isActive 
                    ? 'bg-gradient-to-r from-primary/20 to-primary/5 text-primary-light border border-primary/20 shadow-lg shadow-primary/10' 
                    : 'text-white/50 hover:text-white hover:bg-white/5'
                  }
                `}
              >
                {({ isActive }) => (
                  <>
                    <div className="flex items-center gap-4">
                      <item.icon size={20} className={isActive ? 'text-primary-light' : 'text-white/30 group-hover:text-primary-light transition-colors'} />
                      <span className="font-semibold text-sm">{item.name}</span>
                    </div>
                    {isActive && <ChevronRight size={14} className="animate-pulse" />}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          {/* Footer Sidebar / User Info */}
          <div className="pt-6 border-t border-white/5">
            <div className="flex items-center gap-4 p-4 glass rounded-2xl mb-4 group cursor-pointer hover:bg-white/10 transition-all">
               <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-secondary to-primary text-white flex items-center justify-center font-bold text-lg shadow-inner group-hover:scale-105 transition-transform leading-none uppercase">
                 {user?.nombre?.[0]}
               </div>
               <div className="flex-1 min-w-0">
                  <p className="text-xs font-black truncate text-white">{user?.nombre} {user?.apellido}</p>
                  <p className="text-[10px] text-white/40 uppercase font-bold tracking-widest truncate">{user?.rol_nombre || 'Manager'}</p>
               </div>
               {can('Configuracion') && (
                  <NavLink to="/configuracion" className="text-white/20 hover:text-primary-light transition-colors">
                     <Settings size={16} />
                  </NavLink>
               )}
            </div>
            
            <button 
              onClick={logout}
              className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-white/5 text-white/30 hover:text-red-400 hover:bg-red-500/10 hover:border-red-500/20 transition-all text-xs font-bold uppercase tracking-widest"
            >
              <LogOut size={14} /> Cerrar Sesión
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Dashboard */}
      <main className="flex-1 relative flex flex-col h-screen overflow-hidden">
        {/* Topbar */}
        <header className="h-20 glass border-b border-white/5 flex items-center justify-between px-8 lg:px-12 z-40 shrink-0">
          <div className="flex items-center gap-4 group">
            {/* Toggle Sidebar Button */}
            <button 
              onClick={() => setSidebarOpen(!isSidebarOpen)}
              className="p-2.5 rounded-xl glass text-white/50 hover:text-white transition-all hover:scale-110 active:scale-95 border border-white/5 shadow-xl"
              title={isSidebarOpen ? "Contraer Menú" : "Expandir Menú"}
            >
              {isSidebarOpen ? <X size={18} /> : <Menu size={18} />}
            </button>

            <div className="hidden lg:flex items-center gap-4">
              <Search size={18} className="text-white/30 group-focus-within:text-primary-light transition-colors" />
              <input 
                type="text" 
                placeholder="Buscar en el sistema..." 
                className="bg-transparent border-none outline-none text-sm text-white focus:ring-0 w-64 font-medium"
              />
            </div>
          </div>
          <div className="flex items-center gap-6">
             <div className="flex items-center gap-2 px-3 py-1.5 glass rounded-xl text-[10px] font-black uppercase tracking-widest text-emerald-400 border border-emerald-500/20 shadow-lg shadow-emerald-500/5">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></div>
                Sistema Activo
             </div>
             <button className="relative p-2 rounded-xl glass hover:bg-white/10 text-white/50 hover:text-white transition-all">
                <Bell size={20} />
                <span className="absolute top-2 right-2 w-2 h-2 bg-pink-500 rounded-full border-2 border-[#1a1a2e]"></span>
             </button>
          </div>
        </header>

        {/* Dynamic Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-0">
            {children}
        </div>
      </main>

      <style dangerouslySetInnerHTML={{ __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar-track { background: transparent; }
        .custom-scrollbar-thumb { background: rgba(255,255,255,0.05); border-radius: 10px; }
      `}} />
    </div>
  );
};

export default MainLayout;
